import isEmpty from 'lodash/isEmpty';
import RestClient from '../RestClient/RestClient';
import { criteoApiUrl, partnerId, deviceMapping, pageTypeMapping } from './CriteoApi.config';

const setFilters = (params, sponsoredProductsExcludeFilterParams, lowerEnvQueryConfig) => {
    const filters = [];
    if (params?.Dr && params?.s1_discount_range?.match('Off & More'))
        // eslint-disable-next-line no-param-reassign
        delete params.s1_discount_range;

    Object?.entries(params)?.forEach((property) => {
        if (
            sponsoredProductsExcludeFilterParams?.length === 0 ||
            (sponsoredProductsExcludeFilterParams?.length > 0 &&
                sponsoredProductsExcludeFilterParams.indexOf(property[0]) === -1)
        ) {
            if (property[1] === undefined || property[1] === null) return;
            let filterName = property[0];
            let value = property[1]?.toString();
            const isSingleValue = value?.indexOf('|') === -1;
            const operator = isSingleValue ? 'eq' : 'in';
            let defaultFilterPush = true;

            if (!isSingleValue && filterName !== 's1_discount_range') {
                value = value.split('|');
                if (filterName !== 's1_price_range' && filterName !== 'thread_count')
                    value = value?.join(',');
            }

            // product_size
            if (filterName === 'product_size') {
                filterName = 'SIZE1';
                // capacity
            } else if (filterName === 'capacity') {
                filterName = 'CAPACITY1';
                // gender
            } else if (filterName === 'gender') {
                filterName = 'gender_binned';
                // deals and promotions
            } else if (filterName === 's1_deals_and_promotions') {
                filterName = 'MARKETING_PROMOTIONS';
                value = value?.replace('SALE', 'SALE PRICE');
                // brand
            } else if (filterName === 'brand') {
                filterName = 'brand_binned';
            }

            // thread count bucket
            if (filterName === 'thread_count') {
                filterName = 'thread_count_id';

                // bucket logic
                const threadCountFunc = (val) => {
                    let newValue;
                    switch (val) {
                        case '0-300':
                            newValue = 'under 299';
                            break;
                        case 'under 300':
                            newValue = 'under 299';
                            break;
                        case '300-500':
                            newValue = '300-499';
                            break;
                        case '500-700':
                            newValue = '500-699';
                            break;
                        default:
                            newValue = val;
                    }
                    return newValue;
                };
                value = isSingleValue
                    ? threadCountFunc(value)
                    : value?.map(threadCountFunc)?.join(',');
                value = value?.replace(/ - /g, '-')?.toLowerCase();
            }

            // price range
            if (filterName === 's1_price_range') {
                filterName = 'price_id';
                // bloomreach logic
                const priceRangeFunc = (val) => {
                    let newValue;
                    switch (val) {
                        case 'Over $5000':
                            newValue = 'Over $4999';
                            break;
                        case '$10-$15':
                            newValue = '$10-$14';
                            break;
                        case '$20-$30':
                            newValue = '$20-$24,$25-$29';
                            break;
                        default:
                            newValue = val;
                    }
                    if (newValue?.match(/0+$/)) {
                        const split = newValue.split('-$');
                        // eslint-disable-next-line radix
                        newValue = `${split[0]}-$${parseInt(split[1]) - 1}`;
                    }
                    return newValue;
                };
                value = isSingleValue
                    ? priceRangeFunc(value)
                    : value?.map(priceRangeFunc)?.join(',');
                value = value?.replace(/ - /g, '-')?.toLowerCase();
            }

            // discount range with buckets (bloomreach)
            if (filterName === 's1_discount_range' && !value?.match('Off & More')) {
                filterName = 'discount_id';
                value = !isSingleValue ? value.split('|') : value;
                const discountRangeFunc = (val) => {
                    let newValue = val;
                    const split = newValue.split('%-');
                    newValue = `${split[0]}-${
                        // eslint-disable-next-line radix
                        split[1] === '100%' ? '100' : parseInt(split[1]) - 1
                    }`;
                    return newValue;
                };
                value = isSingleValue
                    ? discountRangeFunc(value)
                    : value?.map(discountRangeFunc)?.join(',');
            }

            // discount range without buckets (solr)
            if (filterName === 's1_discount_range' && value?.match('Off & More')) {
                defaultFilterPush = false;
                value = value?.replace(/% Off & More/g, '');
                value = `${Math.min(...value.split('|'))}`;
                filters.push(
                    `(MIN_PRC_DSCNTD_PC,ge,${Math.max(
                        ...value.split('|')
                    )}),(MIN_PRC_DSCNTD_PC,le,100)`
                );
            }

            // custom price range
            if (filterName === 'Nf' || filterName === 'Dr') {
                defaultFilterPush = false;
                filterName = filterName === 'Nf' ? 'price' : 'MIN_PRC_DSCNTD_PC';
                value = value.split(' ');
                filters.push(`(${filterName},ge,${value[1]}),(${filterName},le,${value[2]})`);
            }

            if (defaultFilterPush) {
                filters.push(`(${filterName},${operator},${value})`);
            }
        }
    });

    if (!isEmpty(lowerEnvQueryConfig)) {
        if (lowerEnvQueryConfig?.isProduction) {
            filters.push(`(lowerenvironment,eq,false)`);
        } else if (lowerEnvQueryConfig?.enableIOverrideCriteoTestCampaing) {
            filters.push(`(lowerenvironment,eq,false)`);
        } else {
            filters.push(`(lowerenvironment,eq,true)`);
        }
    }
    return filters;
};

export const getCriteoApi = (payload, criteoUrl = '') => {
    /* 
    payload => Criteo param
    ---------
    REQUIRED ALL PAGES
    retailVisitorParam => retailer-visitor-id 
    env => environment, page-id
    pageType => page-id, event-type

    OPTIONAL ALL PAGES
    customerId => customer-id
    noLog => noLog
    regionId => regionId

    SRP
    params.searchTerm => keywords (required)
    params.page => page-number (optional)

    PLP
    params.id => category (required)
    params.page => page-number (optional)

    PDP
    parentItem => parent-item (required)
    price => price (required)
    availability => availability (required)
    item => item (optional)

    ORDER CONFIRMATION
    transaction => transaction-id (required)
    item => item (required)
    price => price (required)
    quantity => quantity (required)
    */
    // prevent Criteo call if these required payloads are not sent
    const pageMapping = pageTypeMapping(payload);
    if (!payload?.retailVisitorParam || !payload?.env || !pageMapping) return {};
    if (payload?.pageType === 'Search' && !payload?.params?.searchTerm) return {};
    if (payload?.pageType === 'Gallery' && !payload?.params?.id) return {};
    if (
        payload?.pageType === 'PDP' &&
        !(
            payload?.parentItem &&
            payload?.price &&
            (payload?.availability === 0 || payload?.availability === 1)
        )
    )
        return {};
    if (
        payload?.pageType === 'OrderConfirmation' &&
        !(payload?.transaction && payload?.item && payload?.price && payload?.quantity)
    )
        return {};

    let params = {
        // required params for all pages
        ...(!criteoUrl && { 'criteo-partner-id': partnerId }),
        environment: payload.env,
        'page-id': `${pageMapping.eventType}_API_${deviceMapping[payload.env]}`,
        'event-type': `${pageMapping.eventType}`,
        'retailer-visitor-id': payload.retailVisitorParam,
        regionId: payload?.params?.storeIds ? payload.params.storeIds : 'global',
        // optional params
        ...(payload?.customerId && { 'customer-id': payload.customerId }),
        // required for non-prod envs
        ...(payload?.nolog && { nolog: 1, forceuniversal: 1 }),
        // page specific params
        ...pageMapping.params,
    };

    if (payload?.pageType === 'Search' || payload?.pageType === 'Gallery') {
        const filters = payload?.sponsoredProductsEnableFilters?.includes(payload.pageType)
            ? setFilters(
                  payload?.params,
                  payload?.sponsoredProductsExcludeFilterParams,
                  payload?.lowerEnvQueryConfig
              )
            : [];
        if (filters?.length > 0) {
            params = {
                ...params,
                filters: filters?.join(','),
            };
        }
    }

    if (
        payload?.pageType === 'PDP' ||
        payload?.pageType === 'OrderConfirmation' ||
        payload?.pageType === 'Home'
    ) {
        const filters = setFilters({}, undefined, payload?.lowerEnvQueryConfig);
        if (filters?.length > 0) {
            params = {
                ...params,
                filters: filters?.join(','),
            };
        }
    }

    const config = {
        url: criteoUrl || criteoApiUrl,
        params,
        external: true,
        credentials: 'same-origin',
    };

    return RestClient.get(config).then((response) => response);
};

export default getCriteoApi;
